/**
 * Automation domain helpers
 * - ID generation
 * - default step shape for backend
 * - validation stub
 * - prepare payload for save (strip UI-only fields)
 */

const generateId = () => {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {
        // fallthrough
    }
    // Fallback
    return `id-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
};

/**
 * Valid step types that the backend can execute
 */
const VALID_STEP_TYPES = ['email', 'sms', 'wait', 'webhook', 'trigger'];

/**
 * Trigger types for automation entry points
 */
export const TRIGGER_TYPES = {
    FORM_SUBMIT: 'FORM_SUBMIT',
    MANUAL_ENTRY: 'MANUAL_ENTRY',
};

export const isValidStepType = (type) => {
    if (!type) return false;
    return VALID_STEP_TYPES.includes(type.toLowerCase());
};

export const getDefaultStep = (type) => {
    const id = generateId();
    const normalizedType = type ? type.toLowerCase() : type;
    switch (normalizedType) {
        case 'email':
            // Backend expects: to, subject, body
            return { id, type: 'email', config: { to: '', subject: '', body: '' } };
        case 'sms':
            // Backend expects: to, body
            return { id, type: 'sms', config: { to: '', body: '' } };
        case 'wait':
            // Backend expects: delaySeconds (number)
            return { id, type: 'wait', config: { delaySeconds: 86400 } }; // 1 day default
        case 'trigger':
            // Default trigger config supports both form-based and manual entry
            return {
                id,
                type: 'trigger',
                config: {
                    entryType: 'form', // 'form' | 'manual'
                    // Form-based trigger config
                    formId: null,
                    // Manual entry config (used when entryType === 'manual')
                    leadFilters: {
                        tags: [],
                        status: 'All',
                    },
                    selectedLeadIds: [],
                }
            };
        case 'webhook':
            return { id, type: 'webhook', config: { url: '', method: 'POST', headers: {}, body: {} } };
        default:
            return { id, type, config: {} };
    }
};

export const validateAutomation = (automation) => {
    const errors = [];

    if (!automation) {
        return { valid: false, errors: ['Automation is required'] };
    }

    // Validate trigger
    if (!automation.trigger || !automation.trigger.type) {
        errors.push('Automation must have a trigger with a type');
    }

    // Validate steps
    const steps = automation.steps || [];
    if (steps.length === 0) {
        errors.push('Automation must have at least one step');
    }

    steps.forEach((step, index) => {
        if (!step.type) {
            errors.push(`Step ${index + 1}: missing type`);
        } else if (!isValidStepType(step.type)) {
            errors.push(`Step ${index + 1}: invalid type "${step.type}"`);
        }
    });

    return { valid: errors.length === 0, errors };
};

export const prepareAutomationForSave = (automation) => {
    // Produce a payload that strips UI-only fields like icon/title/subtitle
    const { id, name, description, status, trigger, guardrails } = automation || {};
    const steps = (automation.steps || []).map(s => ({
        id: s.id,
        type: s.type,
        config: s.config || {}
    }));

    return {
        id,
        name,
        description,
        status,
        trigger: trigger || null,
        steps,
        guardrails: guardrails || []
    };
};

export default {
    generateId,
    getDefaultStep,
    isValidStepType,
    validateAutomation,
    prepareAutomationForSave,
    TRIGGER_TYPES,
};
